import React, { useState } from "react";
import { compose, graphql } from "react-apollo";
import Table from "components/Table/Table";
import { withQuery } from "containers/withQuery";
import PencilButton from "components/ActionButtons/PencilButton";
import DeleteButton from "components/ActionButtons/DeleteButton";
import AlertModal from "components/AlertModal/AlertModal";
import translateErrorMessage from "utils/translateErrorMessage";
import { withSnackbarConsumer, Color } from "contexts/SnackbarContext/SnackbarContext.jsx";
import { PLANT_LOCATIONS_QUERY, ACTUATORS_QUERY } from "../generalRegisterQueries";
import { DELETE_PLANT_LOCATION_MUTATION } from "../generalRegisterMutations";
import PlantLocationEdit from "./PlantLocationEdit";
import { plantLocationTypes, arrowPositionTypes } from "../enumTypes";

const headers = [
  {
    label: "Id", key: "id"
  },
  {
    label: "Nome", key: "name",
  },
  {
    label: "Tipo", key: "locationType",
  },
  {
    label: "Materiais", key: "inventoryItems",
  },
  {
    label: "Posição X", key: "positionX",
  },
  {
    label: "Posição Y", key: "positionY",
  },
  {
    label: "Lado de Referência", key: "arrowPosition",
  },
  {
    label: "Expansão Vertical", key: "isContainerVertical",
  },
  {
    label: "Colunas", key: "numberOfColumns",
  },
  {
    label: "", key: "action",
  },
  {
    label: "", key: "delete",
  },
];

const formatItems = (items) => items.join(", ");

const formatNumber = (value, maxDecimals = 3) => {
  const num = Number(value);
  if (Number.isNaN(num)) return null;

  return Number(num.toFixed(maxDecimals)).toString();
}

const PlantLocationsTable = ({
  plantLocations,
  deletePlantLocation,
  snackbarContext,
}) => {
  const [editId, setEditId] = useState(null);
  const locationTypeHandleItems = new Set(["AGGREGATES", "GENERAL"]);

  const openEdit = (id) => {
    setEditId(id);
  };

  const closeEdit = () => {
    setEditId(null);
  };

  const handleDeletePlantLocation = async (id) => {
    try {
      await deletePlantLocation({
        variables: { id },
        refetchQueries: [{
          query: PLANT_LOCATIONS_QUERY
        }, {
          query: ACTUATORS_QUERY
        }],
      });

      snackbarContext.setNotificationTimeOut(
        Color.success,
        "Card deletado."
      );
    } catch (error) {
      snackbarContext.setNotificationTimeOut(
        Color.danger,
        translateErrorMessage(error)
      );
    };
  };

  const handleDeleteModal = (id) => {
    AlertModal.fire({
      title: "Deletar Card",
      html: "Deseja deletar o card selecionado?",
      type: "error",
      showCancelButton: true,
      confirmButtonColor: "#1D84C6",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Voltar",
      reverseButtons: true,
    }).then(({ value }) => {
      if (value) {
        handleDeletePlantLocation(id);
      }
    });
  };

  const buildTableItems = () => (
    [...plantLocations]
      .sort((a, b) => a.id - b.id)
      .map(plantLocation => ({
        ...plantLocation,
        positionX: formatNumber(plantLocation.positionX),
        positionY: formatNumber(plantLocation.positionY),
        locationType: plantLocationTypes[plantLocation.locationType],
        arrowPosition: arrowPositionTypes[plantLocation.arrowPosition],
        isContainerVertical: plantLocation.isContainerVertical ? "Sim" : "Não",
        inventoryItems: (
          locationTypeHandleItems.has(plantLocation.locationType)
          && plantLocation.inventoryItems
          && plantLocation.inventoryItems.length > 0
        ) ? formatItems(plantLocation.inventoryItems.map(({ name }) => name))
          : "-",
        action: (
          <PencilButton
            onPencilEditClick={() => openEdit(plantLocation.id)}
          />
        ),
        delete: (
          <DeleteButton
            onClick={() => handleDeleteModal(plantLocation.id)}
          />
        ),
      }))
  );

  return (
    <React.Fragment>
      <Table
        headers={headers}
        matchHeaders
        items={buildTableItems()}
      />
      {editId && (
        <PlantLocationEdit id={editId} onFinishEdit={closeEdit} />
      )}
    </React.Fragment>
  );
};

export default compose(
  withQuery({ query: PLANT_LOCATIONS_QUERY }),
  graphql(DELETE_PLANT_LOCATION_MUTATION, { name: "deletePlantLocation", }),
  withSnackbarConsumer,
)(PlantLocationsTable);
